<script>
import Switches from "vue-switches"
import { cnpjService } from "@/helpers/easyindustriaapi/cnpj.service"
import { cepService } from "@/helpers/easyindustriaapi/cep.service"
import { isValidCnpj, isValidCep, normalizeCnpjInput, normalizeCep } from "@/helpers/validators/cnpj"
import { applyCepLookup, applyCnpjLookup } from "@/helpers/agreement-lookup"

export default {
  name: 'AgreementNfseFields',
  components: {
    Switches,
  },
  props: {
    convenio: { type: Object, required: true },
    listCodeTax: { type: Array, default: () => [] },
    errors: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      cnpjLoading: false,
      cepLoading: false,
      ufs: [
        'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS',
        'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC',
        'SP', 'SE', 'TO',
      ],
    }
  },
  computed: {
    showNfseFields() {
      return !this.convenio.is_private
    },
    aggregateByPatient: {
      get() {
        return this.convenio.aggregate_by_patient ?? this.convenio.mostrar_paciente_relatorio_nfse ?? false
      },
      set(value) {
        this.$set(this.convenio, 'aggregate_by_patient', Boolean(value))
        this.$set(this.convenio, 'mostrar_paciente_relatorio_nfse', Boolean(value))
      },
    },
  },
  methods: {
    async pesquisarCnpj() {
      const cnpj = normalizeCnpjInput(this.convenio.cnpj)
      if (!isValidCnpj(cnpj)) {
        this.$bvToast.toast('Informe um CNPJ válido com 14 dígitos.', {
          title: 'Atenção',
          variant: 'warning',
          solid: true,
        })
        return
      }

      if (!/^\d+$/.test(cnpj)) {
        this.$bvToast.toast('Consulta automática ainda não suporta CNPJ alfanumérico. Preencha os dados manualmente.', {
          title: 'Atenção',
          variant: 'warning',
          solid: true,
        })
        return
      }

      this.cnpjLoading = true
      try {
        const dados = await cnpjService.getCompanyByCnpj(cnpj)
        if (!dados || dados.status === 'ERROR') {
          this.$bvToast.toast(dados?.message || 'CNPJ não encontrado.', {
            title: 'Atenção',
            variant: 'warning',
            solid: true,
          })
          return
        }

        applyCnpjLookup(this.convenio, dados)

        const cep = String(this.convenio.cep || '').replace(/\D/g, '')
        if (cep.length === 8) {
          await this.pesquisarCep()
        }
      } catch (error) {
        this.$bvToast.toast('Não foi possível consultar o CNPJ.', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.cnpjLoading = false
      }
    },

    async pesquisarCep() {
      const cep = normalizeCep(this.convenio.cep)
      if (!isValidCep(cep)) {
        this.$bvToast.toast('Informe um CEP válido com 8 dígitos.', {
          title: 'Atenção',
          variant: 'warning',
          solid: true,
        })
        return
      }

      this.cepLoading = true
      try {
        const dados = await cepService.getAddressByCep(cep)
        if (!dados || dados.erro) {
          this.$bvToast.toast('CEP não encontrado.', {
            title: 'Atenção',
            variant: 'warning',
            solid: true,
          })
          return
        }

        applyCepLookup(this.convenio, dados)
      } catch (error) {
        this.$bvToast.toast('Não foi possível consultar o CEP.', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.cepLoading = false
      }
    },
  },
}
</script>

<template>
  <div>
    <div class="row mt-3">
      <div class="col-md-4">
        <label for="code_tax_id">Código Fiscal padrão (NFS-e)</label>
        <select
          id="code_tax_id"
          v-model="convenio.code_tax_id"
          class="form-control"
          :class="{ 'is-invalid': errors.code_tax_id }"
        >
          <option :value="null">Selecione o código</option>
          <option
            v-for="codetax in listCodeTax"
            :key="codetax.id"
            :value="codetax.id"
          >
            {{ codetax.id }} - {{ codetax.name }}
          </option>
        </select>
        <small v-if="errors.code_tax_id" class="text-danger">{{ errors.code_tax_id }}</small>
      </div>
      <div class="col-md-4">
        <h5 class="font-size-14 mb-4">Mostrar nome do Paciente no relatório legado?</h5>
        <switches v-model="convenio.is_pacient_view" color="secondary" class="ml-1"></switches>
      </div>
      <div class="col-md-8">
        <h5 class="font-size-14 mb-4">Agrupar NFS-e por paciente neste convênio?</h5>
        <switches v-model="aggregateByPatient" color="primary" class="ml-1"></switches>
        <small class="d-block text-muted">Regra fiscal independente da visibilidade do relatório legado.</small>
      </div>
    </div>

    <div v-if="showNfseFields" class="row mt-2">
      <div class="col-md-12">
        <h5 class="font-size-14 mb-3 text-muted">Dados do tomador para emissão automática de NFS-e</h5>
      </div>
      <div class="col-md-3">
        <label for="convenio_cnpj">CNPJ</label>
        <div class="d-flex">
          <input
            id="convenio_cnpj"
            v-model="convenio.cnpj"
            type="text"
            inputmode="text"
            maxlength="18"
            class="form-control"
            :class="{ 'is-invalid': errors.cnpj }"
            placeholder="00.000.000/0000-00"
            @keyup.enter="pesquisarCnpj"
          />
          <b-button
            variant="outline-primary"
            size="sm"
            class="ml-1"
            title="Buscar dados pelo CNPJ"
            :disabled="cnpjLoading"
            @click="pesquisarCnpj"
          >
            <b-spinner v-if="cnpjLoading" small></b-spinner>
            <span v-else>Buscar</span>
          </b-button>
        </div>
        <small v-if="errors.cnpj" class="text-danger">{{ errors.cnpj }}</small>
      </div>
      <div class="col-md-5">
        <label for="convenio_email">E-mail</label>
        <input
          id="convenio_email"
          v-model="convenio.email"
          type="email"
          class="form-control"
          :class="{ 'is-invalid': errors.email }"
          placeholder="fiscal@convenio.com.br"
        />
        <small v-if="errors.email" class="text-danger">{{ errors.email }}</small>
      </div>
      <div class="col-md-2">
        <label for="convenio_cep">CEP</label>
        <div class="d-flex">
          <input
            id="convenio_cep"
            v-model="convenio.cep"
            type="text"
            maxlength="9"
            class="form-control"
            :class="{ 'is-invalid': errors.cep }"
            placeholder="00000-000"
            @keyup.enter="pesquisarCep"
          />
          <b-button
            variant="outline-primary"
            size="sm"
            class="ml-1"
            title="Buscar endereço pelo CEP"
            :disabled="cepLoading"
            @click="pesquisarCep"
          >
            <b-spinner v-if="cepLoading" small></b-spinner>
            <span v-else>Buscar</span>
          </b-button>
        </div>
        <small v-if="errors.cep" class="text-danger">{{ errors.cep }}</small>
      </div>
      <div class="col-md-2">
        <label for="convenio_uf">UF</label>
        <select
          id="convenio_uf"
          v-model="convenio.uf"
          class="form-control"
          :class="{ 'is-invalid': errors.uf }"
        >
          <option value="">UF</option>
          <option v-for="uf in ufs" :key="uf" :value="uf">{{ uf }}</option>
        </select>
        <small v-if="errors.uf" class="text-danger">{{ errors.uf }}</small>
      </div>
    </div>

    <div v-if="showNfseFields" class="row mt-3">
      <div class="col-md-5">
        <label for="convenio_logradouro">Logradouro</label>
        <input
          id="convenio_logradouro"
          v-model="convenio.logradouro"
          type="text"
          class="form-control"
          :class="{ 'is-invalid': errors.logradouro }"
        />
        <small v-if="errors.logradouro" class="text-danger">{{ errors.logradouro }}</small>
      </div>
      <div class="col-md-2">
        <label for="convenio_numero">Número</label>
        <input
          id="convenio_numero"
          v-model="convenio.numero"
          type="text"
          class="form-control"
          :class="{ 'is-invalid': errors.numero }"
        />
        <small v-if="errors.numero" class="text-danger">{{ errors.numero }}</small>
      </div>
      <div class="col-md-2">
        <label for="convenio_complemento">Complemento</label>
        <input
          id="convenio_complemento"
          v-model="convenio.complemento"
          type="text"
          class="form-control"
        />
      </div>
      <div class="col-md-3">
        <label for="convenio_bairro">Bairro</label>
        <input
          id="convenio_bairro"
          v-model="convenio.bairro"
          type="text"
          class="form-control"
          :class="{ 'is-invalid': errors.bairro }"
        />
        <small v-if="errors.bairro" class="text-danger">{{ errors.bairro }}</small>
      </div>
    </div>

    <div v-if="showNfseFields" class="row mt-3">
      <div class="col-md-3">
        <label for="convenio_codigo_municipio">Código município (IBGE)</label>
        <input
          id="convenio_codigo_municipio"
          v-model="convenio.codigo_municipio"
          type="text"
          maxlength="7"
          class="form-control"
          :class="{ 'is-invalid': errors.codigo_municipio }"
          placeholder="Código IBGE do município"
        />
        <small v-if="errors.codigo_municipio" class="text-danger">{{ errors.codigo_municipio }}</small>
        <small class="text-muted">Informe o município fiscal do convênio; não há município padrão.</small>
      </div>
    </div>
  </div>
</template>
